import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Card, Grid } from "@mui/material";
import { connect } from "react-redux";
import Swal from "sweetalert2";
import MessageTypes from "enums/message-types";

import { useHistory } from "react-router-dom";
import validations from "layouts/class/components/class-details/schemas/classFormValidation";
import initialForm from "layouts/class/components/class-details/schemas/classForm";
import localizeForm from "layouts/class/components/class-details/schemas/classLocalizeForm";
import initialValues from "layouts/class/components/class-details/schemas/classFormInitialValues";
import { removeMessage } from "redux/actions/message";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import { startNewClassScreen, saveClass } from "redux/actions/pages/class";
import { getSubOrganizationListByOrganizationId } from "redux/actions/pages/sub-organization";
import { getBookFormLookups } from "redux/actions/pages";
import ClassForm from "../ClassForm";

const ClassDetails = ({
  localizations,
  message,
  classData,
  allOrganizations,
  removeErrorMsg,
  startScreen,
  saveNewClass,
  subOrganizations,
  fetchSubOrganizations,
  getLookups,
}) => {
  const [form, setForm] = useState(initialForm);
  const history = useHistory();

  const isEditMode = classData && classData?.id > 0;

  useEffect(() => {
    startScreen();
    getLookups();
  }, []);

  useEffect(() => {
    if (localizations) {
      setForm(localizeForm(form, localizations));
    }
  }, [localizations]);

  useEffect(() => {
    if (isEditMode && classData?.organizationId) {
      fetchSubOrganizations({
        organizationId: classData.organizationId,
      });
    }
  }, [classData]);

  useEffect(() => {
    if (
      message &&
      message.messageContent !== undefined &&
      message.messageContent !== null &&
      message.messageContent !== ""
    ) {
      Swal.fire({
        icon: message.messageType || "error",
        title:
          message.messageTitle === MessageTypes.success
            ? localizations?.MESSAGE_SUCCESS
            : localizations?.MESSAGE_ERROR,
        text: message.messageContent,
        showCancelButton: false,
        confirmButtonText: localizations?.MESSAGE_GO_HOME_CONFIRM_BTN,
      }).then(() => {
        removeErrorMsg();
        history.push("/class");
      });
    }
  }, [message]);

  const handleOrganizationChange = (orgId) => {
    if (!isEditMode && orgId) {
      fetchSubOrganizations({
        organizationId: orgId,
      });
    }
  };

  const getInitialValues = () => {
    if (classData && classData?.id > 0) {
      let selectedOrganization = null;
      if (classData.organizationId && Array.isArray(allOrganizations)) {
        const foundOrganization = allOrganizations.find(
          (organization) => organization.id === classData.organizationId
        );
        if (foundOrganization) {
          selectedOrganization = {
            value: foundOrganization.id,
            label: foundOrganization.organizationKey,
          };
        }
      }
      let selectedSubOrganization = null;
      if (classData.subOrganizationId && Array.isArray(subOrganizations)) {
        const foundSubOrganization = subOrganizations.find(
          (subOrganization) => subOrganization.id === classData.subOrganizationId
        );
        if (foundSubOrganization) {
          selectedSubOrganization = {
            value: foundSubOrganization.id,
            label: foundSubOrganization.subOrganizationKey,
          };
        }
      }
      return {
        className: classData.className || "",
        classLevel: classData.classLevel
          ? { value: classData.classLevel, label: classData.classLevel }
          : null,
        organization: selectedOrganization,
        subOrganization: selectedSubOrganization,
      };
    }
    return initialValues;
  };

  const handleSubmit = (values, actions) => {
    const formSubOrgValue = values.subOrganization;
    const safeSubOrgId = formSubOrgValue?.value ?? formSubOrgValue ?? null;

    const payload = {
      classes: {
        subOrganizationId: isEditMode ? classData.subOrganizationId : safeSubOrgId,
        className: values.className ?? "",
        classLevel: values.classLevel?.value ?? "",
        ...(isEditMode && { id: classData.id }),
      },
    };

    saveNewClass(payload);

    actions.setSubmitting(false);
    actions.resetForm();
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={validations.validation}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form id={form.formId} autoComplete="off">
            <Grid container spacing={3}>
              <Grid item xs={12} lg={12}>
                <Card sx={{ overflow: "visible" }}>
                  <SuiBox p={3}>
                    <ClassForm
                      formData={{
                        formField: form.formField,
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        setFieldValue,
                        setFieldTouched,
                      }}
                      allOrganizations={allOrganizations}
                      subOrganizations={subOrganizations}
                      onOrganizationChange={handleOrganizationChange}
                      isEditMode={isEditMode}
                    />
                    <SuiBox display="flex" my={5}>
                      <SuiBox ml="auto">
                        <SuiButton
                          disabled={isSubmitting}
                          type="submit"
                          variant="contained"
                          color="dark"
                          size="small"
                        >
                          {localizations?.FORM_SAVE_BTN}
                        </SuiButton>
                      </SuiBox>
                    </SuiBox>
                  </SuiBox>
                </Card>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

ClassDetails.defaultProps = {
  localizations: null,
  message: null,
  classData: null,
  allOrganizations: null,
  subOrganizations: null,
  fetchSubOrganizations: () => {},
};

ClassDetails.propTypes = {
  localizations: PropTypes.objectOf(PropTypes.any),
  message: PropTypes.objectOf(PropTypes.any),
  removeErrorMsg: PropTypes.func.isRequired,
  startScreen: PropTypes.func.isRequired,
  saveNewClass: PropTypes.func.isRequired,
  classData: PropTypes.objectOf(PropTypes.any),
  allOrganizations: PropTypes.arrayOf(PropTypes.any),
  subOrganizations: PropTypes.arrayOf(PropTypes.any),
  fetchSubOrganizations: PropTypes.func,
  getLookups: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  localizations: state.localization?.pairs,
  allOrganizations: state.data?.organizations ?? [],
  subOrganizations: state.data?.subOrganizations ?? [],
});

const mapDispatchToProps = {
  removeErrorMsg: removeMessage,
  startScreen: startNewClassScreen,
  saveNewClass: saveClass,
  fetchSubOrganizations: getSubOrganizationListByOrganizationId,
  getLookups: getBookFormLookups,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClassDetails);
